import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  Link,
  Typography,
  Button,
  useMediaQuery,
  useTheme,
  MenuItem,
  Toolbar,
  Container,
  Stack,
  Badge,
  Paper,
  Grid,
  Grow,
  Slide,
  alpha,
} from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import CloseIcon from '@mui/icons-material/Close'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import certifications from '../Assets/Images/College_certified.png'
import PhoneIcon from '@mui/icons-material/Phone'
import EmailIcon from '@mui/icons-material/Email'
import CollegeLogo from '../Assets/Images/Header_Logo.png'
import '../Styles/Header.css'
import { styled, keyframes } from '@mui/system'
import { IMAGE_BASE_PATH_COMMON } from '../Config/Config'

const NAV_ITEMS = [
  { id: 1, title: 'Home', pathname: '/home', exact: true },
  {
    id: 2,
    title: 'About',
    pathname: '/aboutus',
    children: [
      { title: 'About Us', pathname: '/aboutus' },
      { title: 'Infrastructure', pathname: '/infrastructure' },
    ],
  },
  { id: 3, title: 'Admissions', pathname: '/admissions' },
  {
    id: 4,
    title: 'Academic',
    children: [
      { title: 'Academic Calendar', pathname: '/academic_calendar' },
      { title: 'Syllabus', pathname: '/syllabus' },
      { title: 'Faculty', pathname: '/faculty' },
      { title: 'Accounts and Admin Staff', pathname: '/accounts_admin_staff' },
    ],
  },
  { id: 6, title: 'Elite Students Program', pathname: '/studentcorner' },
  {
    id: 7,
    title: 'Committees',
    children: [
      {
        title: 'Anti-Ragging cell',
        pathname: '/aicte/anti_ragging_cell/Anti Ragging Committee.pdf',
        disabled: false,
      },
      {
        title: 'Greivance Rederessal cell',
        pathname:
          '/aicte/greivance_rederessal_cell/REDRESSAL GRIEVANCE COMMITTEE.pdf',
        disabled: false,
      },
      {
        title: 'PIO & APIO ',
        pathname: '/aicte/pio_apio/PIO & APIO.pdf',
        disabled: false,
      },
      {
        title: 'women cell',
        pathname: '/aicte/women_cell/Women Protection Cell.pdf',
        disabled: false,
      },
    ],
  },
  {
    id: 8,
    title: 'AICTE',
    children: [
      {
        title: 'EOA',
        pathname: '/aicte/eoa/AICTE/MBA EOA 2025-26.pdf',
        disabled: false,
      },
      {
        title: 'IIC',
        pathname: '/IIC/AICTE/IIC.docx',
        disabled: false,
      },
    ],
  },
  {
    id: 10,
    title: 'Contact us',
    pathname: '/contactus',
    disabled: false,
  },
  {
    id: 11,
    title: 'Activities',
    pathname: '/activity',
    disabled: true,
    badge: true,
  },
  { id: 12, title: 'Student Engagements', pathname: '/student_engagement' },
]

const Header = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const [mobileOpen, setMobileOpen] = useState(false)
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'))
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'))
  const [activeNavItem, setActiveNavItem] = useState(null)
  const [activeSubNav, setActiveSubNav] = useState(null)
  const [isFixed, setIsFixed] = useState(false)
  const headerRef = useRef(null)
  const [scrolled, setScrolled] = useState(false)
  const contactBarRef = useRef(null)
  const logoBarRef = useRef(null)

  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState)
  }

  useEffect(() => {
    let lastScrollY = window.scrollY
    let ticking = false

    const handleScroll = () => {
      lastScrollY = window.scrollY

      if (!ticking) {
        window.requestAnimationFrame(() => {
          if (
            !headerRef.current ||
            !contactBarRef.current ||
            !logoBarRef.current
          )
            return

          const contactHeight = contactBarRef.current.offsetHeight || 0
          const logoHeight = logoBarRef.current.offsetHeight || 0
          const triggerOffset = contactHeight + logoHeight // 👈 total height to trigger

          const scrollTop = lastScrollY

          setIsFixed(scrollTop > triggerOffset)
          setScrolled(scrollTop > 100)
          ticking = false
        })

        ticking = true
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const isMenuItemActive = (item) => {
    if (item.children) {
      return item.children.some((child) => location.pathname === child.pathname)
    }
    return location.pathname === item.pathname
  }

  useEffect(() => {
    setActiveNavItem(null)
    setActiveSubNav(null)
  }, [location.pathname])

  const handleNavigation = (item, subItem = null) => {
    if (subItem) {
      const selected_data = { College: item, CourseOffered: subItem }
      navigate(subItem.pathname, { state: { College_details: selected_data } })
      setActiveSubNav(subItem.title === activeSubNav ? null : subItem.title)
    } else if (item.children) {
      setActiveNavItem(item.title === activeNavItem ? null : item.title)
    } else {
      navigate(item.pathname)
    }
    if (isMobile || isTablet) {
      setMobileOpen(false)
    }
  }

  const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`

  const glow = keyframes`
  0%, 100% { box-shadow: 0 0 0px rgba(0, 0, 0, 0); }
  50% { box-shadow: 0 0 6px rgba(0, 123, 255, 0.5); }
`

  const AnimatedButton = styled(Button)(({ theme }) => ({
    fontSize: '0.35rem',
    padding: '6px 8px',
    minWidth: 'auto',
    lineHeight: 1,
    borderRadius: 4,
    fontWeight: 500,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    animation: `${pulse} 2s ease-in-out infinite, ${glow} 2s ease-in-out infinite`,
    transition: 'transform 0.3s ease',

    '&:hover': {
      transform: 'translateY(-1px)',
      backgroundColor: theme.palette.background.whiteBackground,
    },
  }))

  const renderDesktopNavItem = (item) => (
    <Box
      key={item.id}
      sx={{
        position: 'relative',
        alignItems: 'center',
        '&:hover': {
          '& .nav-item-underline': {
            width: '100%',
            opacity: 1,
          },
        },
      }}
      onMouseEnter={() => item.children && setActiveNavItem(item.id)}
      onMouseLeave={() => item.children && setActiveNavItem(null)}
    >
      <Button
        onClick={() => handleNavigation(item)}
        sx={{
          color: 'white',
          textTransform: 'none',
          px: 1,
          py: 2.5,
          fontWeight: isMenuItemActive(item) ? 'bold' : 'normal',
          position: 'relative',
          fontSize: 'small',
          transition: 'all 0.2s ease',
          '&:hover': {
            color: 'text.inverse',
          },
        }}
      >
        {item.title}
        {item.children && (
          <KeyboardArrowDownIcon
            sx={{
              ml: 0.5,
              transition: 'transform 0.3s ease',
              transform: activeNavItem === item.id ? 'rotate(180deg)' : 'none',
            }}
          />
        )}
        <Box
          className="nav-item-underline"
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            height: 3,
            width: isMenuItemActive(item) ? '100%' : 0,
            backgroundColor: 'background.primaryBackground',
            transition: 'all 0.3s ease',
            opacity: isMenuItemActive(item) ? 1 : 0,
          }}
        />
      </Button>
      {item.children && activeNavItem === item.id && (
        <Grow in={activeNavItem === item.id} timeout={200}>
          <Paper
            elevation={4}
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              backgroundColor: 'background.mainBackground',
              borderRadius: 1,
              zIndex: 1000,
              minWidth: '220px',
              overflow: 'hidden',
              backdropFilter: 'blur(10px)',
            }}
          >
            {item.children.map((child) => (
              <MenuItem
                key={child.title}
                onClick={(e) => {
                  if (child.title === 'Courses') {
                    e.preventDefault()
                    e.stopPropagation()
                  } else {
                    handleNavigation(null, child)
                  }
                }}
                disabled={child.disabled}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 1,
                  px: 2,
                  color: 'text.inverse',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  },
                  '&.Mui-disabled': {
                    opacity: 0.6,
                  },
                }}
              >
                <Box display="flex" alignItems="center">
                  {child.title}
                  {child.badge && (
                    <Badge color="error" variant="dot" sx={{ ml: 1 }} />
                  )}
                </Box>
                {child.children && <KeyboardArrowRightIcon fontSize="small" />}
              </MenuItem>
            ))}
          </Paper>
        </Grow>
      )}
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <CssBaseline />
      {/* Top Contact Bar */}
      <Slide direction="down" in={!scrolled} timeout={300}>
        <AppBar
          ref={contactBarRef}
          position="static"
          sx={{
            backgroundColor: 'background.primaryBackground',
            boxShadow: 'none',
            padding: 0,
            backdropFilter: 'blur(10px)',
          }}
        >
          <Container maxWidth="lg">
            <Toolbar disableGutters variant="dense">
              <Grid container alignItems="center" spacing={1}>
                <Grid item xs={12} sm={6}>
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    flexWrap="wrap"
                    justifyContent={{ xs: 'center', sm: 'flex-start' }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PhoneIcon
                        fontSize="small"
                        sx={{ color: theme.palette.common.white }}
                      />
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{
                          color: theme.palette.common.white,
                          fontSize: { xs: '10px', sm: '12px' },
                        }}
                      >
                        <Link
                          href="tel:040-27670675"
                          color="inherit"
                          underline="none"
                          sx={{
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          040 – 2767 06 75
                        </Link>
                        &nbsp;/&nbsp;
                        <Link
                          href="tel:+919100221282"
                          color="inherit"
                          underline="none"
                          sx={{
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          +91 9100221282
                        </Link>
                        &nbsp;/&nbsp;
                        <Link
                          href="tel:+918331041174"
                          color="inherit"
                          underline="none"
                          sx={{
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          +91 8331041174
                        </Link>
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center">
                      <EmailIcon
                        fontSize="small"
                        sx={{ color: theme.palette.common.white }}
                      />
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{
                          color: theme.palette.common.white,
                          fontSize: { xs: '10px', sm: '12px' },
                        }}
                      >
                        <Link
                          href="mailto:principal.mba@dbraei.edu.in"
                          color="inherit"
                          underline="none"
                          sx={{
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          principal.mba@dbraei.edu.in
                        </Link>
                      </Typography>
                    </Stack>
                  </Stack>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={6}
                  display="flex"
                  justifyContent={{ xs: 'center', sm: 'flex-end' }}
                  gap={2}
                >
                  <AnimatedButton
                    sx={{
                      fontSize: {
                        xs: '0.75rem',
                        sm: '0.575rem',
                        md: '0.875rem',
                      },
                    }}
                  >
                    ICET CODE: BRAB
                  </AnimatedButton>
                </Grid>
              </Grid>
            </Toolbar>
          </Container>
        </AppBar>
      </Slide>

      {/* Logo Bar */}
      <AppBar
        ref={logoBarRef}
        sx={{
          backgroundColor: theme.palette.background.whiteBackground,
          position: 'relative',
          height: '100px',
          maxWidth: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: scrolled ? theme.shadows[4] : 'none',
          transition: 'all 0.3s ease',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, sm: 0 },
              py: 1,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: { xs: 48, sm: 60, md: 80 },
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <img
                  src={`${IMAGE_BASE_PATH_COMMON}Kaka-venkataswamy-2-768x538 1.png`}
                  alt="Founder"
                  style={{
                    height: '100%',
                    width: 'auto',
                    objectFit: 'contain',
                    filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.2))',
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  height: '100%',
                  marginLeft: '16px',
                }}
              >
                <img
                  src={CollegeLogo}
                  alt="College Logo"
                  loading="lazy"
                  style={{
                    height: '80%',
                    maxHeight: '100px',
                    width: '100%',
                    objectFit: 'contain',
                    filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.2))',
                  }}
                />
              </Box>
            </Stack>

            <Box
              sx={{
                display: { xs: 'none', sm: 'flex' },
                alignItems: 'center',
                height: { sm: 48, md: 60 },
              }}
            >
              <img
                src={certifications}
                alt="College Certifications"
                loading="lazy"
                style={{
                  maxHeight: 80,
                  objectFit: 'contain',
                  filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.2))',
                }}
              />
            </Box>
          </Box>
        </Container>
      </AppBar>

      {/* Navigation Bar */}
      <AppBar
        ref={headerRef}
        position={isFixed ? 'fixed' : 'relative'}
        elevation={isFixed ? 4 : 0}
        sx={{
          backgroundColor: alpha(theme.palette.background.mainBackground, 0.95),
          zIndex: theme.zIndex.appBar,
          transition: 'all 0.3s ease',
          py: 0,
          top: isFixed ? 0 : 'auto',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters>
            <Grid container justifyContent="end">
              {isSmallScreen ? (
                <Grid item>
                  <IconButton
                    color="inherit"
                    onClick={handleDrawerToggle}
                    sx={{
                      color: theme.palette.common.white,
                      backgroundColor: alpha(theme.palette.common.white, 0.1),
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.common.white, 0.1),
                      },
                    }}
                  >
                    <MenuIcon />
                  </IconButton>
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                    }}
                  >
                    {NAV_ITEMS.map(renderDesktopNavItem)}
                  </Box>
                </Grid>
              )}
            </Grid>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        transitionDuration={300}
        ModalProps={{
          keepMounted: true,
          BackdropProps: {
            transitionDuration: 300,
          },
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: '280px',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            borderLeft: '1px solid rgba(255,255,255,0.3)',
          },
        }}
      >
        {/* Drawer Header */}
        <CloseIcon
          onClick={handleDrawerToggle}
          style={{
            color: '#555',
            fontSize: '22px',
            '&:hover': { cursor: 'pointer', background: 'rgba(0,0,0,0.1)' },
          }}
        />
        <div
          style={{
            overflowY: 'auto',
            padding: '8px',
            height: '100%',
          }}
        >
          {NAV_ITEMS.map((item) => (
            <div key={item.id}>
              {item.children ? (
                <details
                  style={{
                    margin: '4px 0',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <summary
                    style={{
                      padding: '12px 16px',
                      listStyle: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontWeight: isMenuItemActive(item) ? '600' : '500',
                      color: isMenuItemActive(item) ? '#2c83fb' : '#333',
                      background: isMenuItemActive(item)
                        ? 'rgba(44, 131, 251, 0.1)'
                        : 'none',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: 'rgba(0,0,0,0.03)',
                      },
                    }}
                  >
                    <span>{item.title}</span>
                    <KeyboardArrowDownIcon
                      style={{
                        transition: 'transform 0.3s ease',
                        transform: 'rotate(0deg)',
                        fontSize: '20px',
                        color: '#666',
                      }}
                    />
                  </summary>
                  <div
                    style={{
                      padding: '4px 0 4px 12px',
                      background: 'rgba(0,0,0,0.02)',
                    }}
                  >
                    {item.children.map((child) => (
                      <button
                        key={child.pathname}
                        onClick={() => handleNavigation(item, child)}
                        disabled={child.disabled}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '10px 16px 10px 24px',
                          margin: '2px 0',
                          background:
                            location.pathname === child.pathname
                              ? 'rgba(44, 131, 251, 0.15)'
                              : 'transparent',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: child.disabled ? 'not-allowed' : 'pointer',
                          color:
                            location.pathname === child.pathname
                              ? '#2c83fb'
                              : child.disabled
                              ? '#999'
                              : '#444',
                          fontWeight:
                            location.pathname === child.pathname
                              ? '500'
                              : '400',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          transition: 'all 0.2s ease',
                          opacity: child.disabled ? 0.7 : 1,
                          '&:hover': {
                            background: child.disabled
                              ? 'transparent'
                              : 'rgba(0,0,0,0.05)',
                          },
                        }}
                      >
                        <span>{child.title}</span>
                        {child.badge && (
                          <span
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              background: '#ff4757',
                              marginLeft: '8px',
                            }}
                          ></span>
                        )}
                      </button>
                    ))}
                  </div>
                </details>
              ) : (
                <button
                  onClick={() => handleNavigation(item)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '12px 16px',
                    margin: '4px 0',
                    background: isMenuItemActive(item)
                      ? 'rgba(44, 131, 251, 0.15)'
                      : 'transparent',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    color: isMenuItemActive(item) ? '#2c83fb' : '#333',
                    fontWeight: isMenuItemActive(item) ? '600' : '500',
                    display: 'flex',
                    alignItems: 'center',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: 'rgba(0,0,0,0.03)',
                    },
                  }}
                >
                  {item.title}
                </button>
              )}
              <div
                style={{
                  height: '1px',
                  background: 'rgba(0,0,0,0.05)',
                  margin: '4px 16px',
                }}
              ></div>
            </div>
          ))}
        </div>
      </Drawer>
    </Box>
  )
}

export default React.memo(Header)
